import { useEffect, useMemo, useState } from 'react';
import { api } from '../api.js';

export default function Quiz({
  user,
  course = null,
  onSubmitted,
  reloadKey = 0,
  adminAssistOpen = false,
  onAdminAssistClose = () => {},
  onAdminAssistRevealed = () => {},
}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [attemptInfo, setAttemptInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [revealUsername, setRevealUsername] = useState('');
  const [revealPassword, setRevealPassword] = useState('');
  const [revealError, setRevealError] = useState('');
  const [revealBusy, setRevealBusy] = useState(false);
  const [revealed, setRevealed] = useState(null); // { [questionId]: 'B' }
  const [sessionId, setSessionId] = useState(() => {
    try { return sessionStorage.getItem('ibma_assessment_session_id') || null; } catch { return null; }
  });
  const [unitCode, setUnitCode] = useState(() => {
    try { return sessionStorage.getItem('ibma_assessment_unit') || null; } catch { return null; }
  });

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    setAnswers({});
    setRevealed(null);
    setRevealUsername('');
    setRevealPassword('');
    setRevealError('');
    (async () => {
      try {
        const data = await api.getQuestions(unitCode || undefined, course);
        if (cancelled) return;
        setQuestions(data.questions || []);
        setAttemptInfo({
          attemptNumber: data.attemptNumber,
          maxAttempts: data.maxAttempts,
          previousAttempts: data.previousAttempts || [],
        });
        // Store the server-generated session ID for secure submission.
        if (data.sessionId) {
          setSessionId(data.sessionId);
          try { sessionStorage.setItem('ibma_assessment_session_id', data.sessionId); } catch {}
        }
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [reloadKey, unitCode]);

  const allAnswered = useMemo(
    () => questions.length > 0 && questions.every((q) => answers[q.id] != null),
    [questions, answers]
  );

  function setAnswer(qid, optionKey) {
    setAnswers((prev) => ({ ...prev, [qid]: optionKey }));
  }

  async function handleSubmit() {
    if (!allAnswered) return;
    if (!confirm('Submit your answers? This will count as one of your attempts.')) return;
    setBusy(true);
    setError('');
    try {
      // Send only id + chosen answer key. correctAnswer is locked server-side via sessionId.
      const payload = questions.map((q) => ({ id: q.id, answer: answers[q.id] }));
      const result = await api.submit(payload, unitCode || undefined, sessionId, course);
      onSubmitted(result);
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  }

  async function handleReveal(e) {
    e.preventDefault();
    if (!revealUsername || !revealPassword) return;
    setRevealBusy(true);
    setRevealError('');
    try {
      const ids = questions.map((q) => q.id);
      const result = await api.revealAnswers(revealUsername, revealPassword, ids, unitCode || undefined, sessionId, course);
      setRevealed(result.answers || {});
      setRevealUsername('');
      setRevealPassword('');
      onAdminAssistRevealed();
    } catch (err) {
      setRevealError(err.message);
    } finally {
      setRevealBusy(false);
    }
  }

  function cancelReveal() {
    setRevealUsername('');
    setRevealPassword('');
    setRevealError('');
    onAdminAssistClose();
  }

  if (loading) return <div className="card center">Loading questions…</div>;
  if (error) return <div className="card"><div className="error">{error}</div></div>;
  if (questions.length === 0) return <div className="card">No questions available for your course.</div>;

  const answeredCount = Object.keys(answers).length;

  return (
    <div className="card">
      {adminAssistOpen && (
        <form className="reveal-prompt" onSubmit={handleReveal}>
          <div className="reveal-prompt-row">
            <input
              id="reveal-username"
              type="text"
              aria-label="Admin username"
              autoFocus
              autoComplete="off"
              value={revealUsername}
              onChange={(e) => setRevealUsername(e.target.value)}
              placeholder="Admin username"
            />
            <input
              id="reveal-password"
              type="password"
              aria-label="Admin password"
              autoComplete="off"
              value={revealPassword}
              onChange={(e) => setRevealPassword(e.target.value)}
              placeholder="Admin password"
            />
            <button type="submit" disabled={!revealUsername || !revealPassword || revealBusy}>
              {revealBusy ? 'Checking…' : 'Reveal'}
            </button>
            <button type="button" className="secondary" onClick={cancelReveal}>
              Cancel
            </button>
          </div>
          {revealError && <div className="error">{revealError}</div>}
        </form>
      )}

      {attemptInfo && (
        <div className="attempt-banner">
          Attempt {attemptInfo.attemptNumber} of {attemptInfo.maxAttempts}
        </div>
      )}
      <h2 style={{ marginTop: 8 }}>
        {unitCode ? `${course || user.course} — ${unitCode}` : (course || user.course)} — Assessment
      </h2>
      <p className="muted">Answer all {questions.length} questions, then submit.</p>

      {revealed && (
        <div className="attempt-banner reveal-banner">
          Correct answers are highlighted (admin override)
        </div>
      )}

      {questions.map((q, idx) => (
        <div className="question" key={q.id}>
          <h3>{idx + 1}. {q.question}</h3>
          {Object.entries(q.options).map(([key, text]) => (
            <label
              className={`option${revealed && revealed[q.id] === key ? ' option-correct' : ''}`}
              key={key}
            >
              <input
                type="radio"
                name={`q_${q.id}`}
                value={key}
                checked={answers[q.id] === key}
                onChange={() => setAnswer(q.id, key)}
              />
              <span><strong>{key}.</strong> {text}</span>
              {revealed && revealed[q.id] === key && <span className="hint">Correct</span>}
            </label>
          ))}
        </div>
      ))}

      <div className="footer-actions">
        <div className="progress">{answeredCount} / {questions.length} answered</div>
        <button onClick={handleSubmit} disabled={!allAnswered || busy}>
          {busy ? 'Submitting…' : 'Submit'}
        </button>
      </div>
      {error && <div className="error">{error}</div>}
    </div>
  );
}
